/**
 * Lunar Data — Loads landing sites and named features for Moon mode.
 * Builds a location index for flyTo navigation on the Moon globe.
 */
import landingSitesData from '../../data/lunar/landing-sites.json';
import namedFeaturesData from '../../data/lunar/named-features.json';

let locationIndex = null;

/**
 * Build a Map<string, { center, zoom }> for all lunar locations.
 */
function buildLunarLocationIndex() {
  if (locationIndex) return locationIndex;

  const index = new Map();

  // Landing sites
  for (const f of landingSitesData.features) {
    const p = f.properties;
    const [lng, lat] = f.geometry.coordinates;
    const zoom = 6;

    // Index by mission name, site name, and common aliases
    const keys = [
      p.name.toLowerCase(),
      p.mission.toLowerCase(),
    ];
    if (p.region) keys.push(p.region.toLowerCase());

    for (const key of keys) {
      index.set(key, { center: [lng, lat], zoom, feature: f });
    }
  }

  // Named features
  for (const f of namedFeaturesData.features) {
    const p = f.properties;
    const [lng, lat] = f.geometry.coordinates;
    // Zoom based on feature size
    let zoom = 5;
    if (p.diameter_km && p.diameter_km < 100) zoom = 7;
    if (p.diameter_km && p.diameter_km < 50) zoom = 8;
    if (p.type === 'mountain' || p.type === 'valley' || p.type === 'rille' || p.type === 'escarpment') zoom = 7;

    const keys = [p.name.toLowerCase()];
    if (p.englishName) keys.push(p.englishName.toLowerCase());

    for (const key of keys) {
      index.set(key, { center: [lng, lat], zoom, feature: f });
    }
  }

  // Common aliases
  const aliases = {
    'sea of tranquility': 'mare tranquillitatis',
    'sea of serenity': 'mare serenitatis',
    'sea of rains': 'mare imbrium',
    'ocean of storms': 'oceanus procellarum',
    'sea of crises': 'mare crisium',
    'sea of fertility': 'mare fecunditatis',
    'sea of nectar': 'mare nectaris',
    'sea of clouds': 'mare nubium',
    'sea of moisture': 'mare humorum',
    'sea of cold': 'mare frigoris',
    'lunar alps': 'montes alpes',
    'lunar apennines': 'montes apenninus',
    'alpine valley': 'vallis alpes',
    'straight wall': 'rupes recta',
    'hadley rille': 'rima hadley',
    'far side': null, // handled separately
    'dark side': null,
    'near side': null,
  };

  // Add region views
  index.set('far side', { center: [180, 0], zoom: 1.5, feature: null });
  index.set('dark side', { center: [180, 0], zoom: 1.5, feature: null });
  index.set('near side', { center: [0, 0], zoom: 1.5, feature: null });
  index.set('south pole', { center: [0, -90], zoom: 4, feature: null });
  index.set('north pole', { center: [0, 90], zoom: 4, feature: null });

  for (const [alias, target] of Object.entries(aliases)) {
    if (target && index.has(target)) {
      index.set(alias, index.get(target));
    }
  }

  locationIndex = index;
  return index;
}

/**
 * Find a lunar location by name (fuzzy match).
 */
function findLunarLocation(name) {
  const idx = buildLunarLocationIndex();
  const lower = name.toLowerCase().trim();

  // Exact match
  if (idx.has(lower)) return idx.get(lower);

  // Partial match
  for (const [key, value] of idx) {
    if (key.includes(lower) || lower.includes(key)) return value;
  }

  return null;
}

/**
 * Get all landing sites, optionally filtered.
 */
function getLandingSites(filter = null) {
  let features = landingSitesData.features;

  if (filter) {
    features = features.filter(f => {
      const p = f.properties;
      if (filter.country) return p.country.toLowerCase() === filter.country.toLowerCase();
      if (filter.year) return p.year === filter.year;
      if (filter.category) return p.category === filter.category;
      if (filter.mission) return p.mission.toLowerCase().includes(filter.mission.toLowerCase());
      if (filter.agency) return p.agency.toLowerCase().includes(filter.agency.toLowerCase());
      if (filter.country_year) {
        const [country, year] = filter.country_year.split('_');
        return p.country.toLowerCase() === country.toLowerCase() && p.year === parseInt(year);
      }
      return true;
    });
  }

  return features;
}

/**
 * Get named features, optionally filtered by type.
 */
function getNamedFeatures(type = null) {
  if (!type) return namedFeaturesData.features;
  return namedFeaturesData.features.filter(f => f.properties.type === type);
}

/**
 * Get all features (landing sites + named features).
 */
function getAllFeatures() {
  return [
    ...landingSitesData.features,
    ...namedFeaturesData.features,
  ];
}

export {
  buildLunarLocationIndex,
  findLunarLocation,
  getLandingSites,
  getNamedFeatures,
  getAllFeatures,
  landingSitesData,
  namedFeaturesData,
};
